/**
 * @fileoverview WebApp Móvil del Aficionado para la dinámica Esquivador Personal (Personal Surfer - HU-06).
 * Experiencia vertical interactiva a 60 FPS con control 100% por gestos Swipe en Canvas 2.5D,
 * motor de audio sintetizado Web Audio API, feedback háptico, telemetría dual GTM/GA4 y backend,
 * y formulario de captura de leads blindado con One-Time Token (OTT).
 * 
 * @module components/Dashboard/Sections/PersonalSurferFan
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import Confetti from 'react-confetti';
import {
    GetPublicConfig,
    RegisterParticipant,
    SubmitScore
} from '../../../utilities/DL/PersonalSurfer';
import {
    resolveAssetUrl,
    sanitizeRoomId,
    trackDualTelemetry,
    formatScore,
    getSurferBadge,
    validateRegistrationForm,
    saveParticipantSession,
    getParticipantSession,
    clearParticipantSession,
    requestScreenWakeLock,
    releaseScreenWakeLock,
    triggerHapticFeedback,
    createSynthesizedAudioEngine
} from '../../../utilities/BL/PersonalSurfer';
import PersonalSurferRankingModal from './PersonalSurferRankingModal';

export const PersonalSurferFan = ({ roomId: propRoomId }) => {
    const location = useLocation();

    // =========================================================================
    // 1. RESOLUCIÓN DE IDENTIFICADOR DE SALA (ROOM ID) Y ORIGEN
    // =========================================================================
    const activeRoomId = useMemo(() => {
        const queryParams = new URLSearchParams(location.search);
        const rawId = (
            propRoomId ||
            queryParams.get('roomId') ||
            queryParams.get('room') ||
            (typeof localStorage !== 'undefined' ? localStorage.getItem('active_room_id') : '') ||
            ''
        );
        return sanitizeRoomId(rawId);
    }, [propRoomId, location.search]);

    const sourceOrigin = useMemo(() => {
        const queryParams = new URLSearchParams(location.search);
        return queryParams.get('source') || queryParams.get('s') || 'direct';
    }, [location.search]);

    const sessionIdRef = useRef('psl_sess_' + Math.random().toString(36).substring(2, 10));

    // =========================================================================
    // 2. MÁQUINA DE ESTADOS Y CONTROL DE FLUJO
    // Fases: 'LOADING' | 'CLOSED' | 'FORM_SCREEN' | 'COUNTDOWN' | 'PLAYING' | 'SUBMITTING' | 'RESULT_SCREEN'
    // =========================================================================
    const [gameState, setGameState] = useState('LOADING');
    const [configData, setConfigData] = useState(null);
    const [activeTheme, setActiveTheme] = useState(null);

    // Formulario de registro
    const [formData, setFormData] = useState({
        participantName: '',
        phone: '',
        email: '',
        acceptedTerms: false,
        acceptedPrivacy: false
    });
    const [formErrors, setFormErrors] = useState({});
    const [isSubmittingForm, setIsSubmittingForm] = useState(false);
    const hasFiredFormStartRef = useRef(false);

    // Sesión de participante autenticado
    const [participantSession, setParticipantSession] = useState(null);

    // Conteo regresivo
    const [countdownVal, setCountdownVal] = useState(3);

    // Motor de audio
    const audioEngineRef = useRef(null);
    const [isMuted, setIsMuted] = useState(false);

    // Modal de ranking en vivo
    const [isRankingOpen, setIsRankingOpen] = useState(false);

    // Resultado final de juego
    const [gameResult, setGameResult] = useState(null);

    // Dimensiones de ventana para Canvas y Confetti
    const [screenDims, setScreenDims] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 375,
        height: typeof window !== 'undefined' ? window.innerHeight : 667
    });

    // =========================================================================
    // 3. REFERENCIAS Y MOTOR DE CANVAS 2.5D
    // =========================================================================
    const canvasRef = useRef(null);
    const animFrameIdRef = useRef(null);

    // Estado interno mutable de la partida en ejecución
    const runStateRef = useRef({
        score: 0,
        timeRemaining: 30,
        survivalTime: 0,
        coinsCollected: 0,
        bonusesCollected: 0,
        obstaclesDodged: 0,
        collisionsCount: 0,
        currentLane: 1, // 0: Left, 1: Center, 2: Right
        targetLane: 1,
        laneX: 0.5, // 0.0 a 1.0 interpolado
        isJumping: false,
        jumpProgress: 0,
        isSliding: false,
        slideProgress: 0,
        isInvulnerable: false,
        invulnerableTimer: 0,
        screenShake: 0,
        gameSpeed: 6,
        obstacles: [],
        collectibles: [],
        floatingTexts: [],
        particles: [],
        lastFrameTime: 0,
        spawnTimer: 0,
        distanceProgress: 0
    });

    // =========================================================================
    // 4. INICIALIZACIÓN DE AUDIO, WAKE LOCK Y TELEMETRÍA INICIAL
    // =========================================================================
    useEffect(() => {
        const engine = createSynthesizedAudioEngine();
        audioEngineRef.current = engine;
        setIsMuted(engine.isMuted());

        const handleResize = () => {
            if (typeof window !== 'undefined') {
                setScreenDims({
                    width: window.innerWidth,
                    height: window.innerHeight
                });
            }
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('resize', handleResize);
        }

        // Telemetría: qr_scan / page_view
        if (activeRoomId) {
            const initialStep = sourceOrigin === 'qr_scan' ? 'qr_scan' : 'page_view';
            trackDualTelemetry({
                roomId: activeRoomId,
                sessionId: sessionIdRef.current,
                stepName: initialStep,
                source: sourceOrigin
            });
        }

        return () => {
            engine.dispose();
            releaseScreenWakeLock();
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', handleResize);
            }
            if (animFrameIdRef.current) {
                cancelAnimationFrame(animFrameIdRef.current);
            }
        };
    }, [activeRoomId, sourceOrigin]);

    // =========================================================================
    // 5. CARGA DE CONFIGURACIÓN PÚBLICA Y TEMA ACTIVO
    // =========================================================================
    const loadConfiguration = useCallback(async () => {
        try {
            const res = await GetPublicConfig(activeRoomId || null);
            if (res && res.success && res.data) {
                const conf = res.data;
                setConfigData(conf);
                setActiveTheme(conf.activeTheme || null);

                if (!conf.isGameActive) {
                    setGameState('CLOSED');
                    return;
                }

                // Verificar sesión previa almacenada
                const existingSession = getParticipantSession();
                if (existingSession && existingSession.participantId && !conf.allowMultipleAttempts) {
                    setParticipantSession(existingSession);
                    // Si ya jugó y no permite reintentos, permitir consultar resultados
                    setGameState('FORM_SCREEN');
                } else {
                    setGameState('FORM_SCREEN');
                }
            } else {
                setGameState('CLOSED');
            }
        } catch (err) {
            console.warn('[PersonalSurferFan] Error loading config:', err);
            setGameState('CLOSED');
        }
    }, [activeRoomId]);

    useEffect(() => {
        loadConfiguration();
    }, [loadConfiguration]);

    // =========================================================================
    // 6. GESTIÓN DEL FORMULARIO DE CAPTURA DE LEADS
    // =========================================================================
    const handleFormFocus = () => {
        if (!hasFiredFormStartRef.current && activeRoomId) {
            hasFiredFormStartRef.current = true;
            trackDualTelemetry({
                roomId: activeRoomId,
                sessionId: sessionIdRef.current,
                stepName: 'form_start',
                source: sourceOrigin
            });
        }
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const validation = validateRegistrationForm(formData);
        if (!validation.isValid) {
            setFormErrors(validation.errors);
            triggerHapticFeedback('fault');
            return;
        }

        setIsSubmittingForm(true);
        try {
            if (audioEngineRef.current) {
                await audioEngineRef.current.unlock();
            }
            await requestScreenWakeLock();

            const payload = {
                roomId: activeRoomId,
                participantName: formData.participantName.trim(),
                phone: formData.phone.trim(),
                email: formData.email ? formData.email.trim() : null,
                acceptedTerms: formData.acceptedTerms,
                acceptedPrivacy: formData.acceptedPrivacy
            };

            const res = await RegisterParticipant(payload, activeRoomId);
            if (res && res.success && res.data) {
                const session = {
                    participantId: res.data.participantId,
                    oneTimeToken: res.data.oneTimeToken,
                    participantName: res.data.participantName,
                    registeredAt: new Date().toISOString()
                };

                setParticipantSession(session);
                saveParticipantSession(session);

                // Telemetría: form_complete
                trackDualTelemetry({
                    roomId: activeRoomId,
                    sessionId: sessionIdRef.current,
                    stepName: 'form_complete',
                    source: sourceOrigin,
                    additionalData: { participantId: session.participantId }
                });

                // Iniciar flujo de conteo 3-2-1
                startCountdown();
            } else {
                setFormErrors({ general: res.message || 'No se pudo completar el registro. Intenta de nuevo.' });
            }
        } catch (err) {
            console.error('[PersonalSurferFan] Error registering lead:', err);
            setFormErrors({ general: 'Error de conexión. Verifica tu internet.' });
        } finally {
            setIsSubmittingForm(false);
        }
    };

    // =========================================================================
    // 7. FLUJO DE CONTEO REGRESIVO (3, 2, 1, ¡SURF!)
    // =========================================================================
    const startCountdown = () => {
        setGameState('COUNTDOWN');
        setCountdownVal(3);
        if (audioEngineRef.current) {
            audioEngineRef.current.playCountBeep(3);
        }
        triggerHapticFeedback(25);

        let current = 3;
        const interval = setInterval(() => {
            current -= 1;
            if (current > 0) {
                setCountdownVal(current);
                if (audioEngineRef.current) {
                    audioEngineRef.current.playCountBeep(current);
                }
                triggerHapticFeedback(25);
            } else if (current === 0) {
                setCountdownVal('¡SURF!');
                if (audioEngineRef.current) {
                    audioEngineRef.current.playCountBeep('GO');
                }
                triggerHapticFeedback('victory');
            } else {
                clearInterval(interval);
                initAndStartGame();
            }
        }, 850);
    };

    // =========================================================================
    // 8. MOTOR DE JUEGO VERTICAL 2.5D (CANVAS 60 FPS)
    // =========================================================================
    const initAndStartGame = () => {
        const timeLimit = configData?.timeLimitSeconds || 30;
        const baseSpd = configData?.baseSpeed || 6;

        runStateRef.current = {
            score: 0,
            timeRemaining: timeLimit,
            survivalTime: 0,
            coinsCollected: 0,
            bonusesCollected: 0,
            obstaclesDodged: 0,
            collisionsCount: 0,
            currentLane: 1,
            targetLane: 1,
            laneX: 0.5,
            isJumping: false,
            jumpProgress: 0,
            isSliding: false,
            slideProgress: 0,
            isInvulnerable: false,
            invulnerableTimer: 0,
            screenShake: 0,
            gameSpeed: baseSpd,
            obstacles: [],
            collectibles: [],
            floatingTexts: [],
            particles: [],
            lastFrameTime: performance.now(),
            spawnTimer: 0,
            distanceProgress: 0
        };

        setGameState('PLAYING');

        // Telemetría: game_start
        trackDualTelemetry({
            roomId: activeRoomId,
            sessionId: sessionIdRef.current,
            stepName: 'game_start',
            source: sourceOrigin,
            additionalData: { participantId: participantSession?.participantId }
        });
    };

    // Gestos táctiles Swipe
    const touchStartRef = useRef({ x: 0, y: 0, time: 0 });

    const handleTouchStart = (e) => {
        if (e.touches && e.touches.length > 0) {
            touchStartRef.current = {
                x: e.touches[0].clientX,
                y: e.touches[0].clientY,
                time: performance.now()
            };
        }
    };

    const handleTouchEnd = (e) => {
        if (!e.changedTouches || e.changedTouches.length === 0) return;
        const deltaX = e.changedTouches[0].clientX - touchStartRef.current.x;
        const deltaY = e.changedTouches[0].clientY - touchStartRef.current.y;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);
        const minSwipeDist = 28;

        if (absX < minSwipeDist && absY < minSwipeDist) return;

        if (absX > absY) {
            // Swipe horizontal (Izquierda / Derecha)
            if (deltaX < 0) {
                // Swipe Left
                moveLane(-1);
            } else {
                // Swipe Right
                moveLane(1);
            }
        } else {
            // Swipe vertical (Arriba = Salto, Abajo = Deslizamiento)
            if (deltaY < 0) {
                // Swipe Up -> Salto
                triggerJump();
            } else {
                // Swipe Down -> Deslizamiento / Agacharse
                triggerSlide();
            }
        }
    };

    const moveLane = (dir) => {
        const state = runStateRef.current;
        const nextLane = Math.max(0, Math.min(2, state.currentLane + dir));
        if (nextLane !== state.currentLane) {
            state.currentLane = nextLane;
            state.targetLane = nextLane;
            if (audioEngineRef.current) audioEngineRef.current.playSwipe();
            triggerHapticFeedback('swipe');
        }
    };

    const triggerJump = () => {
        const state = runStateRef.current;
        if (!state.isJumping && !state.isSliding) {
            state.isJumping = true;
            state.jumpProgress = 0;
            if (audioEngineRef.current) audioEngineRef.current.playJump();
            triggerHapticFeedback('jump');
        }
    };

    const triggerSlide = () => {
        const state = runStateRef.current;
        if (!state.isSliding && !state.isJumping) {
            state.isSliding = true;
            state.slideProgress = 0;
            if (audioEngineRef.current) audioEngineRef.current.playSlide();
            triggerHapticFeedback('slide');
        }
    };

    // Teclado para pruebas y accesibilidad (Flechas / WASD / Espacio)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (gameState !== 'PLAYING') return;
            if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
                moveLane(-1);
            } else if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
                moveLane(1);
            } else if (e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W' || e.key === ' ') {
                e.preventDefault();
                triggerJump();
            } else if (e.key === 'ArrowDown' || e.key === 's' || e.key === 'S') {
                e.preventDefault();
                triggerSlide();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [gameState]);

    // Bucle principal de Renderizado y Física a 60 FPS
    useEffect(() => {
        if (gameState !== 'PLAYING') return;

        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let isRunning = true;

        const lanePositionsX = [0.22, 0.50, 0.78]; // Fracciones horizontales de carril
        const themePrimary = activeTheme?.primaryColor || '#00e5ff';
        const themeAccent = activeTheme?.accentColor || '#ff007f';
        const winMode = configData?.winMode || 'sudden_death';
        const ptsPerTick = configData?.pointsPerTick || 10;
        const ptsPerCoin = configData?.pointsPerCoin || 100;
        const ptsPerBonus = configData?.pointsPerBonus || 500;
        const penaltyPerHit = configData?.penaltyPerHit || 150;

        const loop = (now) => {
            if (!isRunning) return;

            const state = runStateRef.current;
            const deltaMs = Math.min(64, now - state.lastFrameTime);
            const dt = deltaMs / 1000;
            state.lastFrameTime = now;

            // 1. ACTUALIZAR TIEMPO Y DISTANCIA
            state.timeRemaining -= dt;
            state.survivalTime += dt;
            state.distanceProgress += state.gameSpeed * dt;

            // Incremento progresivo de velocidad si está en modo progresivo
            if (configData?.difficultyMode === 'progressive') {
                state.gameSpeed = (configData?.baseSpeed || 6) + (state.survivalTime * 0.15);
            }

            // Puntos por tick continuo
            state.score += Math.round(ptsPerTick * dt * (state.gameSpeed / 5));

            // Easing suave horizontal de carril
            const targetX = lanePositionsX[state.currentLane];
            state.laneX += (targetX - state.laneX) * Math.min(1, 14 * dt);

            // Físicas de Salto (arco parabólico)
            if (state.isJumping) {
                state.jumpProgress += dt * 2.2;
                if (state.jumpProgress >= 1) {
                    state.isJumping = false;
                    state.jumpProgress = 0;
                }
            }

            // Físicas de Deslizamiento
            if (state.isSliding) {
                state.slideProgress += dt * 2.4;
                if (state.slideProgress >= 1) {
                    state.isSliding = false;
                    state.slideProgress = 0;
                }
            }

            // Invulnerabilidad temporal tras choque en modo penalty
            if (state.isInvulnerable) {
                state.invulnerableTimer -= dt;
                if (state.invulnerableTimer <= 0) {
                    state.isInvulnerable = false;
                }
            }

            // Reducción de temblor de pantalla
            if (state.screenShake > 0) {
                state.screenShake = Math.max(0, state.screenShake - dt * 25);
            }

            // 2. SPAWN DE OBSTÁCULOS Y MONEDAS
            state.spawnTimer += dt;
            const spawnInterval = Math.max(0.65, 1.6 - (state.gameSpeed * 0.05));
            if (state.spawnTimer >= spawnInterval) {
                state.spawnTimer = 0;
                const lane = Math.floor(Math.random() * 3);
                const rand = Math.random();

                if (rand < 0.45) {
                    // Obstáculo bajo (debe saltarse) o alto (debe agacharse) o bloque
                    const obsType = Math.random() < 0.4 ? 'low' : (Math.random() < 0.7 ? 'high' : 'block');
                    state.obstacles.push({
                        id: Math.random(),
                        lane,
                        y: -50,
                        type: obsType,
                        passed: false
                    });
                } else if (rand < 0.85) {
                    // Moneda coleccionable
                    state.collectibles.push({
                        id: Math.random(),
                        lane,
                        y: -40,
                        type: 'coin',
                        rot: 0
                    });
                } else {
                    // Bonus especial
                    state.collectibles.push({
                        id: Math.random(),
                        lane,
                        y: -40,
                        type: 'bonus',
                        rot: 0
                    });
                }
            }

            // 3. ACTUALIZACIÓN Y COLISIONES
            const playerY = canvas.height * 0.78;
            const playerHitboxRadius = canvas.width * 0.09;

            // Actualizar obstáculos
            for (let i = state.obstacles.length - 1; i >= 0; i--) {
                const obs = state.obstacles[i];
                obs.y += state.gameSpeed * 100 * dt;

                const obsX = canvas.width * lanePositionsX[obs.lane];
                const distY = Math.abs(obs.y - playerY);
                const isSameLane = state.currentLane === obs.lane;

                // Detección de colisión
                if (isSameLane && distY < 35 && !state.isInvulnerable) {
                    let hit = true;
                    if (obs.type === 'low' && state.isJumping && state.jumpProgress > 0.15 && state.jumpProgress < 0.85) {
                        hit = false; // Saltó el obstáculo bajo exitosamente
                    } else if (obs.type === 'high' && state.isSliding && state.slideProgress > 0.1 && state.slideProgress < 0.9) {
                        hit = false; // Se deslizó bajo el obstáculo alto exitosamente
                    }

                    if (hit) {
                        state.collisionsCount += 1;
                        state.screenShake = 18;
                        if (audioEngineRef.current) audioEngineRef.current.playCrash();
                        triggerHapticFeedback('crash');

                        if (winMode === 'sudden_death') {
                            // Muerte súbita instantánea
                            state.obstacles.splice(i, 1);
                            endRun('CRASH_SUDDEN_DEATH');
                            return;
                        } else {
                            // Penalización de puntos e invulnerabilidad 1.2s
                            state.score = Math.max(0, state.score - penaltyPerHit);
                            state.isInvulnerable = true;
                            state.invulnerableTimer = 1.2;
                            state.floatingTexts.push({
                                text: `-${penaltyPerHit}`,
                                x: obsX,
                                y: playerY,
                                color: '#ff3366',
                                life: 1.0
                            });
                            state.obstacles.splice(i, 1);
                            continue;
                        }
                    }
                }

                if (obs.y > canvas.height + 60) {
                    state.obstaclesDodged += 1;
                    state.obstacles.splice(i, 1);
                }
            }

            // Actualizar coleccionables
            for (let i = state.collectibles.length - 1; i >= 0; i--) {
                const item = state.collectibles[i];
                item.y += state.gameSpeed * 100 * dt;
                item.rot += dt * 4;

                const itemX = canvas.width * lanePositionsX[item.lane];
                const dist = Math.hypot(itemX - (canvas.width * state.laneX), item.y - playerY);

                if (dist < playerHitboxRadius + 20) {
                    if (item.type === 'coin') {
                        state.coinsCollected += 1;
                        state.score += ptsPerCoin;
                        state.floatingTexts.push({
                            text: `+${ptsPerCoin}`,
                            x: itemX,
                            y: item.y,
                            color: '#ffd700',
                            life: 1.0
                        });
                        if (audioEngineRef.current) audioEngineRef.current.playCoin();
                        triggerHapticFeedback('coin');
                    } else if (item.type === 'bonus') {
                        state.bonusesCollected += 1;
                        state.score += ptsPerBonus;
                        state.floatingTexts.push({
                            text: `¡BONUS +${ptsPerBonus}!`,
                            x: itemX,
                            y: item.y,
                            color: '#00e5ff',
                            life: 1.2
                        });
                        if (audioEngineRef.current) audioEngineRef.current.playBonus();
                        triggerHapticFeedback('bonus');
                    }

                    // Partículas de recolección
                    for (let p = 0; p < 8; p++) {
                        state.particles.push({
                            x: itemX,
                            y: item.y,
                            vx: (Math.random() - 0.5) * 160,
                            vy: (Math.random() - 0.5) * 160,
                            color: item.type === 'coin' ? '#ffd700' : '#00e5ff',
                            life: 0.6
                        });
                    }

                    state.collectibles.splice(i, 1);
                    continue;
                }

                if (item.y > canvas.height + 60) {
                    state.collectibles.splice(i, 1);
                }
            }

            // Actualizar partículas
            for (let i = state.particles.length - 1; i >= 0; i--) {
                const p = state.particles[i];
                p.x += p.vx * dt;
                p.y += p.vy * dt;
                p.life -= dt * 2.0;
                if (p.life <= 0) state.particles.splice(i, 1);
            }

            // Actualizar textos flotantes
            for (let i = state.floatingTexts.length - 1; i >= 0; i--) {
                const ft = state.floatingTexts[i];
                ft.y -= 45 * dt;
                ft.life -= dt * 1.3;
                if (ft.life <= 0) state.floatingTexts.splice(i, 1);
            }

            // Verificar condición de fin de tiempo
            if (state.timeRemaining <= 0) {
                state.timeRemaining = 0;
                endRun('TIME_LIMIT_REACHED');
                return;
            }

            // 4. RENDERIZADO VISUAL EN CANVAS
            renderScene(ctx, canvas, state, themePrimary, themeAccent);

            animFrameIdRef.current = requestAnimationFrame(loop);
        };

        animFrameIdRef.current = requestAnimationFrame(loop);

        return () => {
            isRunning = false;
            if (animFrameIdRef.current) cancelAnimationFrame(animFrameIdRef.current);
        };
    }, [gameState, configData, activeTheme]);

    // Función de renderizado gráfico de alta fidelidad 2.5D
    const renderScene = (ctx, canvas, state, primary, accent) => {
        const w = canvas.width;
        const h = canvas.height;

        ctx.save();

        // Efecto Screen Shake
        if (state.screenShake > 0) {
            const shakeX = (Math.random() - 0.5) * state.screenShake;
            const shakeY = (Math.random() - 0.5) * state.screenShake;
            ctx.translate(shakeX, shakeY);
        }

        // A. Fondo de Pista / Cielo Futurista
        const bgGrad = ctx.createLinearGradient(0, 0, 0, h);
        bgGrad.addColorStop(0, '#0a0d18');
        bgGrad.addColorStop(0.4, '#13192e');
        bgGrad.addColorStop(1, '#080a14');
        ctx.fillStyle = bgGrad;
        ctx.fillRect(0, 0, w, h);

        // Líneas de velocidad de fondo (Estrellas / Pista en movimiento)
        ctx.fillStyle = 'rgba(0, 229, 255, 0.25)';
        const offsetGrid = (state.distanceProgress * 80) % 60;
        for (let y = offsetGrid; y < h; y += 60) {
            ctx.fillRect(0, y, w, 2);
        }

        // B. Los 3 Carriles con perspectiva 2.5D
        const laneBounds = [0, w * 0.333, w * 0.666, w];
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(0, 229, 255, 0.4)';
        ctx.setLineDash([15, 15]);
        ctx.lineDashOffset = -(state.distanceProgress * 50) % 30;

        ctx.beginPath();
        ctx.moveTo(laneBounds[1], 0);
        ctx.lineTo(laneBounds[1], h);
        ctx.moveTo(laneBounds[2], 0);
        ctx.lineTo(laneBounds[2], h);
        ctx.stroke();
        ctx.setLineDash([]); // Reset dash

        // Brillo de carriles
        ctx.fillStyle = 'rgba(0, 229, 255, 0.04)';
        ctx.fillRect(laneBounds[state.currentLane], 0, w * 0.333, h);

        // C. Renderizar Obstáculos
        state.obstacles.forEach((obs) => {
            const obsX = w * (obs.lane === 0 ? 0.22 : (obs.lane === 1 ? 0.50 : 0.78));
            const obsY = obs.y;

            ctx.save();
            ctx.translate(obsX, obsY);

            if (obs.type === 'low') {
                // Valla baja brillante (Amarilla / Naranja)
                ctx.fillStyle = '#ff9900';
                ctx.shadowColor = '#ff9900';
                ctx.shadowBlur = 12;
                ctx.fillRect(-w * 0.11, -12, w * 0.22, 24);

                ctx.fillStyle = '#111';
                ctx.fillRect(-w * 0.08, -6, w * 0.16, 12);

                ctx.fillStyle = '#ffffff';
                ctx.font = 'bold 11px Montserrat, sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText('⚡ SALTA ⚡', 0, 4);
            } else if (obs.type === 'high') {
                // Letrero alto flotante (Morado / Magenta)
                ctx.fillStyle = '#d900ff';
                ctx.shadowColor = '#d900ff';
                ctx.shadowBlur = 15;
                ctx.fillRect(-w * 0.12, -22, w * 0.24, 20);

                ctx.fillStyle = '#ffffff';
                ctx.font = 'bold 10px Montserrat, sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText('⬇️ AGACHA ⬇️', 0, -8);
            } else {
                // Barrera Bloque Total (Rojo Neón)
                ctx.fillStyle = '#ff0033';
                ctx.shadowColor = '#ff0033';
                ctx.shadowBlur = 18;
                ctx.fillRect(-w * 0.12, -20, w * 0.24, 40);

                ctx.fillStyle = '#ffffff';
                ctx.font = 'bold 12px Montserrat, sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText('⛔ ESQUIVA ⛔', 0, 5);
            }

            ctx.restore();
        });

        // D. Renderizar Coleccionables (Monedas y Bonus)
        state.collectibles.forEach((item) => {
            const itemX = w * (item.lane === 0 ? 0.22 : (item.lane === 1 ? 0.50 : 0.78));
            const itemY = item.y;

            ctx.save();
            ctx.translate(itemX, itemY);

            if (item.type === 'coin') {
                // Moneda de oro giratoria 3D
                const scaleX = Math.cos(item.rot);
                ctx.scale(scaleX, 1);
                ctx.fillStyle = '#ffd700';
                ctx.shadowColor = '#ffd700';
                ctx.shadowBlur = 15;
                ctx.beginPath();
                ctx.arc(0, 0, 16, 0, Math.PI * 2);
                ctx.fill();

                ctx.fillStyle = '#b8860b';
                ctx.beginPath();
                ctx.arc(0, 0, 11, 0, Math.PI * 2);
                ctx.fill();

                ctx.fillStyle = '#ffffff';
                ctx.font = 'bold 12px Montserrat, sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText('$', 0, 4);
            } else {
                // Estrella Bonus Holográfica
                ctx.rotate(item.rot);
                ctx.fillStyle = '#00e5ff';
                ctx.shadowColor = '#00e5ff';
                ctx.shadowBlur = 20;

                ctx.beginPath();
                for (let i = 0; i < 5; i++) {
                    ctx.lineTo(Math.cos((18 + i * 72) * Math.PI / 180) * 18, -Math.sin((18 + i * 72) * Math.PI / 180) * 18);
                    ctx.lineTo(Math.cos((54 + i * 72) * Math.PI / 180) * 8, -Math.sin((54 + i * 72) * Math.PI / 180) * 8);
                }
                ctx.closePath();
                ctx.fill();
            }

            ctx.restore();
        });

        // E. Renderizar Partículas
        state.particles.forEach((p) => {
            ctx.save();
            ctx.fillStyle = p.color;
            ctx.shadowColor = p.color;
            ctx.shadowBlur = 8;
            ctx.beginPath();
            ctx.arc(p.x, p.y, 3 * p.life, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        });

        // F. Renderizar Personaje Surfista / Corredor
        const px = w * state.laneX;
        let py = h * 0.78;
        let scale = 1.0;

        if (state.isJumping) {
            const jumpHeight = Math.sin(state.jumpProgress * Math.PI) * 70;
            py -= jumpHeight;
            scale = 1.0 + (jumpHeight / 200);
        } else if (state.isSliding) {
            scale = 0.65;
            py += 15;
        }

        ctx.save();
        ctx.translate(px, py);
        ctx.scale(scale, scale);

        // Efecto parpadeo de invulnerabilidad
        if (state.isInvulnerable && Math.floor(Date.now() / 80) % 2 === 0) {
            ctx.globalAlpha = 0.4;
        }

        // Sombra en el suelo
        ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
        ctx.beginPath();
        ctx.ellipse(0, 30 + (state.isJumping ? (h * 0.78 - py) : 0), 22, 8, 0, 0, Math.PI * 2);
        ctx.fill();

        // Tabla de surf / Deslizador aerodinámico
        ctx.fillStyle = primary;
        ctx.shadowColor = primary;
        ctx.shadowBlur = 18;
        ctx.beginPath();
        ctx.ellipse(0, 20, 28, 10, 0, 0, Math.PI * 2);
        ctx.fill();

        // Cuerpo del Surfista / Personaje
        ctx.fillStyle = accent;
        ctx.shadowColor = accent;
        ctx.shadowBlur = 12;
        ctx.beginPath();
        ctx.arc(0, -5, 14, 0, Math.PI * 2); // Cabeza / Casco
        ctx.fill();

        // Tronco y estela
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(-6, 8, 12, 12);

        ctx.restore();

        // G. Renderizar Textos Flotantes (+100, +500, etc.)
        state.floatingTexts.forEach((ft) => {
            ctx.save();
            ctx.globalAlpha = Math.max(0, ft.life);
            ctx.fillStyle = ft.color;
            ctx.shadowColor = ft.color;
            ctx.shadowBlur = 10;
            ctx.font = '900 18px Montserrat, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(ft.text, ft.x, ft.y);
            ctx.restore();
        });

        ctx.restore();
    };

    // =========================================================================
    // 9. CONCLUSIÓN DE CARRERA Y ENVÍO DE SCORE
    // =========================================================================
    const endRun = async (reason) => {
        setGameState('SUBMITTING');
        const state = runStateRef.current;

        const payload = {
            participantId: participantSession?.participantId,
            oneTimeToken: participantSession?.oneTimeToken,
            score: Math.max(0, Math.round(state.score)),
            survivalTimeSeconds: Math.round(state.survivalTime),
            coinsCollected: state.coinsCollected,
            bonusesCollected: state.bonusesCollected,
            obstaclesDodged: state.obstaclesDodged,
            collisionsCount: state.collisionsCount
        };

        try {
            const res = await SubmitScore(payload, activeRoomId);
            const resData = res?.data || {};

            const finalResult = {
                score: payload.score,
                survivalTimeSeconds: payload.survivalTimeSeconds,
                coinsCollected: payload.coinsCollected,
                bonusesCollected: payload.bonusesCollected,
                rankingPosition: resData.rankingPosition || 1,
                isWinner: Boolean(resData.isWinner),
                badge: getSurferBadge(payload.score),
                reason
            };

            setGameResult(finalResult);
            setGameState('RESULT_SCREEN');

            if (audioEngineRef.current) {
                audioEngineRef.current.playVictory();
            }
            triggerHapticFeedback('victory');

            // Telemetría: game_complete
            trackDualTelemetry({
                roomId: activeRoomId,
                sessionId: sessionIdRef.current,
                stepName: 'game_complete',
                source: sourceOrigin,
                additionalData: {
                    score: payload.score,
                    rankingPosition: finalResult.rankingPosition,
                    participantId: participantSession?.participantId
                }
            });
        } catch (err) {
            console.error('[PersonalSurferFan] Error submitting score:', err);
            // Fallback de resultado local
            setGameResult({
                score: payload.score,
                survivalTimeSeconds: payload.survivalTimeSeconds,
                coinsCollected: payload.coinsCollected,
                bonusesCollected: payload.bonusesCollected,
                rankingPosition: 1,
                badge: getSurferBadge(payload.score),
                reason
            });
            setGameState('RESULT_SCREEN');
        }
    };

    const handleRetry = () => {
        if (configData?.allowMultipleAttempts) {
            startCountdown();
        } else {
            clearParticipantSession();
            setParticipantSession(null);
            setFormData({
                participantName: '',
                phone: '',
                email: '',
                acceptedTerms: false,
                acceptedPrivacy: false
            });
            setGameState('FORM_SCREEN');
        }
    };

    const toggleMute = () => {
        if (audioEngineRef.current) {
            const next = !isMuted;
            audioEngineRef.current.setMuted(next);
            setIsMuted(next);
        }
    };

    // =========================================================================
    // 10. RENDERIZADO PRINCIPAL DE PANTALLAS
    // =========================================================================
    return (
        <div className="psl-surfer-fan">
            {/* Cabecera / Barra Superior Compartida */}
            <header className="psl-surfer-fan__header">
                <div className="psl-surfer-fan__brand">
                    <span className="psl-surfer-fan__brand-icon">🏄‍♂️</span>
                    <span className="psl-surfer-fan__brand-name">ESQUIVADOR PERSONAL</span>
                </div>
                <div className="psl-surfer-fan__header-actions">
                    <button
                        type="button"
                        className="psl-surfer-fan__icon-btn"
                        onClick={toggleMute}
                        aria-label={isMuted ? 'Activar sonido' : 'Silenciar'}
                    >
                        <i className={`bi ${isMuted ? 'bi-volume-mute-fill text-danger' : 'bi-volume-up-fill text-info'}`} />
                    </button>
                    <button
                        type="button"
                        className="psl-surfer-fan__icon-btn"
                        onClick={() => setIsRankingOpen(true)}
                        aria-label="Ver Leaderboard"
                    >
                        <i className="bi bi-trophy-fill text-warning" />
                    </button>
                </div>
            </header>

            {/* PANTALLA 1: CARGANDO */}
            {gameState === 'LOADING' && (
                <main className="psl-surfer-fan__center-card">
                    <div className="spinner-border text-info" style={{ width: '3rem', height: '3rem' }} role="status" />
                    <h2 className="psl-surfer-fan__title mt-3">Cargando la pista...</h2>
                    <p className="psl-surfer-fan__subtitle">Preparando sensores y velocidad</p>
                </main>
            )}

            {/* PANTALLA 2: JUEGO CERRADO O PAUSADO */}
            {gameState === 'CLOSED' && (
                <main className="psl-surfer-fan__center-card">
                    <span style={{ fontSize: '3.5rem' }}>⏸️</span>
                    <h2 className="psl-surfer-fan__title">Dinámica Pausada</h2>
                    <p className="psl-surfer-fan__subtitle">
                        El Esquivador Personal no está activo en este momento. ¡Permanece atento a la pantalla del estadio!
                    </p>
                    <button
                        type="button"
                        className="psl-surfer-btn psl-surfer-btn--primary mt-3"
                        onClick={loadConfiguration}
                    >
                        Reintentar Conexión
                    </button>
                </main>
            )}

            {/* PANTALLA 3: FORMULARIO DE CAPTURA DE LEADS */}
            {gameState === 'FORM_SCREEN' && (
                <main className="psl-surfer-fan__form-container">
                    <div className="psl-surfer-fan__hero-badge">
                        <span>🏆 ¡COMPITE POR EL RÉCORD DEL ESTADIO!</span>
                    </div>

                    <h1 className="psl-surfer-fan__form-title">
                        Registro de Corredor
                    </h1>
                    <p className="psl-surfer-fan__form-desc">
                        Ingresa tus datos para registrar tu puntaje en el ranking gigante del estadio.
                    </p>

                    {formErrors.general && (
                        <div className="alert alert-danger py-2 px-3 mb-3" role="alert">
                            {formErrors.general}
                        </div>
                    )}

                    <form onSubmit={handleFormSubmit} onFocus={handleFormFocus} className="psl-surfer-form">
                        <div className="psl-surfer-form__group">
                            <label className="psl-surfer-form__label" htmlFor="participantName">
                                Nombre y Apellido *
                            </label>
                            <input
                                id="participantName"
                                name="participantName"
                                type="text"
                                className={`psl-surfer-form__input ${formErrors.participantName ? 'is-invalid' : ''}`}
                                placeholder="Ej. Carlos Mendoza"
                                value={formData.participantName}
                                onChange={handleFormChange}
                                maxLength={100}
                                required
                            />
                            {formErrors.participantName && (
                                <div className="invalid-feedback d-block">{formErrors.participantName}</div>
                            )}
                        </div>

                        <div className="psl-surfer-form__group">
                            <label className="psl-surfer-form__label" htmlFor="phone">
                                WhatsApp (para entrega de premios) *
                            </label>
                            <input
                                id="phone"
                                name="phone"
                                type="tel"
                                className={`psl-surfer-form__input ${formErrors.phone ? 'is-invalid' : ''}`}
                                placeholder="Ej. 55 1234 5678"
                                value={formData.phone}
                                onChange={handleFormChange}
                                maxLength={15}
                                required
                            />
                            {formErrors.phone && (
                                <div className="invalid-feedback d-block">{formErrors.phone}</div>
                            )}
                        </div>

                        <div className="psl-surfer-form__group">
                            <label className="psl-surfer-form__label" htmlFor="email">
                                Correo Electrónico (opcional)
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                className={`psl-surfer-form__input ${formErrors.email ? 'is-invalid' : ''}`}
                                placeholder="usuario@correo.com"
                                value={formData.email}
                                onChange={handleFormChange}
                                maxLength={150}
                            />
                            {formErrors.email && (
                                <div className="invalid-feedback d-block">{formErrors.email}</div>
                            )}
                        </div>

                        <div className="psl-surfer-form__checkbox-group">
                            <label className="psl-surfer-form__checkbox-label">
                                <input
                                    type="checkbox"
                                    name="acceptedTerms"
                                    checked={formData.acceptedTerms}
                                    onChange={handleFormChange}
                                />
                                <span>Acepto los Términos y Condiciones de la dinámica. *</span>
                            </label>
                            {formErrors.acceptedTerms && (
                                <div className="invalid-feedback d-block">{formErrors.acceptedTerms}</div>
                            )}

                            <label className="psl-surfer-form__checkbox-label mt-2">
                                <input
                                    type="checkbox"
                                    name="acceptedPrivacy"
                                    checked={formData.acceptedPrivacy}
                                    onChange={handleFormChange}
                                />
                                <span>Acepto la Política de Privacidad y Tratamiento de Datos. *</span>
                            </label>
                            {formErrors.acceptedPrivacy && (
                                <div className="invalid-feedback d-block">{formErrors.acceptedPrivacy}</div>
                            )}
                        </div>

                        <button
                            type="submit"
                            className="psl-surfer-btn psl-surfer-btn--primary psl-surfer-btn--block mt-4"
                            disabled={isSubmittingForm}
                        >
                            {isSubmittingForm ? 'Iniciando Carrera...' : '¡ENTRAR A LA PISTA! 🚀'}
                        </button>
                    </form>
                </main>
            )}

            {/* PANTALLA 4: CONTEO REGRESIVO */}
            {gameState === 'COUNTDOWN' && (
                <div className="psl-surfer-fan__countdown-overlay">
                    <div className="psl-surfer-fan__countdown-number">
                        {countdownVal}
                    </div>
                    <div className="psl-surfer-fan__countdown-tip">
                        <span>Desliza tu dedo en la pantalla para Esquivar, Saltar y Agacharte</span>
                    </div>
                </div>
            )}

            {/* PANTALLA 5: JUEGO VERTICAL 2.5D CANVAS Y HUD */}
            {gameState === 'PLAYING' && (
                <main className="psl-surfer-fan__game-container">
                    {/* HUD Flotante Superior */}
                    <div className="psl-surfer-fan__hud">
                        <div className="psl-surfer-fan__hud-card">
                            <span className="psl-surfer-fan__hud-label">PUNTOS</span>
                            <span className="psl-surfer-fan__hud-value text-info">
                                {formatScore(runStateRef.current.score)}
                            </span>
                        </div>
                        <div className="psl-surfer-fan__hud-card">
                            <span className="psl-surfer-fan__hud-label">TIEMPO</span>
                            <span className={`psl-surfer-fan__hud-value ${runStateRef.current.timeRemaining <= 5 ? 'text-danger fw-bold animate-pulse' : 'text-warning'}`}>
                                {Math.max(0, Math.ceil(runStateRef.current.timeRemaining))}s
                            </span>
                        </div>
                        <div className="psl-surfer-fan__hud-card">
                            <span className="psl-surfer-fan__hud-label">MONEDAS</span>
                            <span className="psl-surfer-fan__hud-value text-success">
                                🪙 {runStateRef.current.coinsCollected}
                            </span>
                        </div>
                    </div>

                    {/* Canvas Vertical Interactivo */}
                    <canvas
                        ref={canvasRef}
                        width={screenDims.width}
                        height={screenDims.height}
                        className="psl-surfer-fan__canvas"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    />

                    {/* Barra de Guía de Gestos Flotante */}
                    <div className="psl-surfer-fan__gesture-guide">
                        <span>⬅️ Carril Izq</span>
                        <span>⬆️ Saltar</span>
                        <span>⬇️ Agacharse</span>
                        <span>➡️ Carril Der</span>
                    </div>
                </main>
            )}

            {/* PANTALLA 6: GUARDANDO RESULTADOS */}
            {gameState === 'SUBMITTING' && (
                <main className="psl-surfer-fan__center-card">
                    <div className="spinner-border text-info" style={{ width: '3rem', height: '3rem' }} role="status" />
                    <h2 className="psl-surfer-fan__title mt-3">Calculando Posición...</h2>
                    <p className="psl-surfer-fan__subtitle">Verificando récord con el servidor del estadio</p>
                </main>
            )}

            {/* PANTALLA 7: PANTALLA DE RESULTADOS Y RANKING */}
            {gameState === 'RESULT_SCREEN' && gameResult && (
                <main className="psl-surfer-fan__results-container">
                    <Confetti
                        width={screenDims.width}
                        height={screenDims.height}
                        numberOfPieces={100}
                        recycle={false}
                        gravity={0.15}
                    />

                    <div className="psl-surfer-fan__results-card">
                        <div className="psl-surfer-fan__results-badge-wrap">
                            <span className="psl-surfer-fan__results-badge-icon">
                                {gameResult.badge?.icon || '🏆'}
                            </span>
                        </div>

                        <h2 className="psl-surfer-fan__results-title">
                            ¡Carrera Finalizada!
                        </h2>
                        <span className="psl-surfer-fan__results-badge-title" style={{ color: gameResult.badge?.color }}>
                            {gameResult.badge?.title}
                        </span>

                        {/* Puntaje Principal */}
                        <div className="psl-surfer-fan__results-score-box">
                            <span className="psl-surfer-fan__results-score-label">PUNTAJE FINAL</span>
                            <span className="psl-surfer-fan__results-score-number">
                                {formatScore(gameResult.score)} pts
                            </span>
                        </div>

                        {/* Resumen de Estadísticas */}
                        <div className="psl-surfer-fan__results-stats-grid">
                            <div className="psl-surfer-fan__results-stat-item">
                                <span className="psl-surfer-fan__stat-label">Posición</span>
                                <span className="psl-surfer-fan__stat-val text-warning">
                                    #{gameResult.rankingPosition}
                                </span>
                            </div>
                            <div className="psl-surfer-fan__results-stat-item">
                                <span className="psl-surfer-fan__stat-label">Tiempo</span>
                                <span className="psl-surfer-fan__stat-val">
                                    {gameResult.survivalTimeSeconds}s
                                </span>
                            </div>
                            <div className="psl-surfer-fan__results-stat-item">
                                <span className="psl-surfer-fan__stat-label">Monedas</span>
                                <span className="psl-surfer-fan__stat-val text-success">
                                    🪙 {gameResult.coinsCollected}
                                </span>
                            </div>
                            <div className="psl-surfer-fan__results-stat-item">
                                <span className="psl-surfer-fan__stat-label">Bonus</span>
                                <span className="psl-surfer-fan__stat-val text-info">
                                    ⭐ {gameResult.bonusesCollected}
                                </span>
                            </div>
                        </div>

                        {/* Acciones */}
                        <div className="psl-surfer-fan__results-actions mt-4">
                            <button
                                type="button"
                                className="psl-surfer-btn psl-surfer-btn--primary psl-surfer-btn--block"
                                onClick={() => setIsRankingOpen(true)}
                            >
                                🏆 Ver Leaderboard del Estadio
                            </button>

                            <button
                                type="button"
                                className="psl-surfer-btn psl-surfer-btn--outline psl-surfer-btn--block mt-2"
                                onClick={handleRetry}
                            >
                                🔄 Jugar Otra Carrera
                            </button>
                        </div>
                    </div>
                </main>
            )}

            {/* Modal de Ranking en vivo */}
            <PersonalSurferRankingModal
                isOpen={isRankingOpen}
                onClose={() => setIsRankingOpen(false)}
                roomId={activeRoomId}
                currentParticipantId={participantSession?.participantId}
            />
        </div>
    );
};

PersonalSurferFan.propTypes = {
    roomId: PropTypes.string
};

export default PersonalSurferFan;
